import { StateEffect, StateField } from "@codemirror/state";
import { type KeywordsType, type SDK_Colors, type SDK_Fonts, filterTemplate, strings } from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";

// pass any vars into CodeMirror from the outside
export interface AppVars {
  stackId: string;
  stackCfKey: string | false;
}
export const appVarsEffect = StateEffect.define<AppVars>();

export const appVarsStateField = StateField.define<AppVars>({
  create() {
    return { stackId: strings.unset, stackCfKey: strings.unset };
  },
  update(appVars, transaction) {
    for (const e of transaction.effects) {
      if (e.is(appVarsEffect)) {
        return e.value;            // new value coming from strings.events.updateAppVars
      }
    }
    return appVars;                // unchanged when no effect present
  },
});

export const backLinksEffect = StateEffect.define<string[]>();

export const backLinksStateField = StateField.define<string[]>({
  create() {
    return [];
  },
  update(backLinks, transaction) {
    for (const e of transaction.effects) {
      if (e.is(backLinksEffect)) {
        return e.value;
      }
    }
    return backLinks;
  },
});

export const editorIsNewEffect = StateEffect.define<boolean>();

export const editorIsNewStateField = StateField.define<boolean>({
  create() {
    return true;
  },
  update(isNew, transaction) {
    if (transaction.isUserEvent("select")) {
      return false;
    }
    return isNew;
  },
});

export const audioFilesEffect = StateEffect.define<QpFile[]>();

export const audioFilesStateField = StateField.define<QpFile[]>({
  create() {
    return [];
  },
  update(files, transaction) {
    for (const e of transaction.effects) {
      if (e.is(audioFilesEffect)) {
        return e.value;
      }
    }
    return files;
  },
});


export const imageFilesEffect = StateEffect.define<QpFile[]>();

export const imageFilesStateField = StateField.define<QpFile[]>({
  create() {
    return [];
  },
  update(files, transaction) {
    for (const e of transaction.effects) {
      if (e.is(imageFilesEffect)) {
        return e.value;
      }
    }
    return files;
  },
});

export const dataFilesEffect = StateEffect.define<QpFile[]>();

export const dataFilesStateField = StateField.define<QpFile[]>({
  create() {
    return [];
  },
  update(files, transaction) {
    for (const e of transaction.effects) {
      if (e.is(dataFilesEffect)) {
        return e.value;
      }
    }
    return files;
  },
});

export const filterNamesEffect = StateEffect.define<string[]>();

export const filterNamesStateField = StateField.define<string[]>({
  create() {
    return [];
  },
  update(filterNames, transaction) {
    for (const e of transaction.effects) {
      if (e.is(filterNamesEffect)) {
        return e.value;
      }
    }
    return filterNames;
  },
});

export const filterColorsEffect = StateEffect.define<SDK_Colors>();

export const defaultFilterColors: SDK_Colors = filterTemplate.colors;


export const filterColorsStateField = StateField.define<SDK_Colors>({
  create() {
    return defaultFilterColors
  },
  update(colors, transaction) {
    for (const e of transaction.effects) {
      if (e.is(filterColorsEffect)) {
        return e.value ?? defaultFilterColors
      }
    }

    if (!colors) return defaultFilterColors

    // biome-ignore lint/suspicious/noExplicitAny: Not even AI can type this
    const filtered: any = {}

    const keys = Object.keys(colors)

    for (const key of keys) {
      const k = key as keyof typeof colors
      if (colors[k]) {
        filtered[k] = colors[k]
      }
    }

    return filtered as SDK_Colors
  },
});

export const loadedFilterFontsEffect = StateEffect.define<SDK_Fonts>();

export const loadedFilterFontsStateField = StateField.define<SDK_Fonts>({
  create() {
    return { first: "" };
  },
  update(loadedFilterFonts, transaction) {
    for (const e of transaction.effects) {
      if (e.is(loadedFilterFontsEffect)) {
        return e.value;
      }
    }
    return loadedFilterFonts;
  },
});

export const mousedOverHelperEffect = StateEffect.define<KeywordsType | undefined>();

export const mousedOverHelperField = StateField.define<KeywordsType | undefined>({
  create() {
    return undefined;
  },
  update(name, transaction) {
    for (const e of transaction.effects) {
      if (e.is(mousedOverHelperEffect)) {
        return e.value;
      }
    }
    return name;
  },
});
